// أدوات النص المنسّق (وصف التاسك): تنظيف HTML قبل العرض، تحويل النص العادي
// القديم إلى HTML، وتحويل الروابط الطويلة إلى روابط مختصرة قابلة للنقر.
// التنظيف ضروري لأن textHtml يُعرض بـdangerouslySetInnerHTML.

const ALLOWED_TAGS = new Set([
  "B", "STRONG", "I", "EM", "U", "S", "STRIKE", "BR", "P", "DIV", "SPAN",
  "FONT", "OL", "UL", "LI", "A", "H1", "H2", "H3", "BLOCKQUOTE",
]);

const ALLOWED_STYLES = new Set([
  "color", "background-color", "font-size", "font-weight", "font-style",
  "text-decoration", "text-decoration-line", "text-align",
]);

const URL_RE = /(https?:\/\/[^\s<]+)/g;

const isSafeUrl = (url) => /^(https?:|mailto:)/i.test(String(url || "").trim());

const cleanStyle = (styleText) =>
  String(styleText || "")
    .split(";")
    .map((rule) => rule.trim())
    .filter(Boolean)
    .filter((rule) => {
      const [prop, ...rest] = rule.split(":");
      const value = rest.join(":").toLowerCase();
      return (
        ALLOWED_STYLES.has(prop.trim().toLowerCase()) &&
        !value.includes("url(") &&
        !value.includes("expression")
      );
    })
    .join("; ");

// نص الرابط الظاهر: الدومين + المسار، بدون باراميترات طويلة (مثل fbclid)
export const shortLinkLabel = (url, max = 45) => {
  try {
    const u = new URL(url);
    const label = (u.hostname + u.pathname).replace(/\/$/, "");
    return label.length > max ? label.slice(0, max) + "…" : label;
  } catch {
    return url.length > max ? url.slice(0, max) + "…" : url;
  }
};

const decorateLink = (a, href) => {
  a.setAttribute("href", href);
  a.setAttribute("target", "_blank");
  a.setAttribute("rel", "noopener noreferrer");
  a.setAttribute("title", href);
  // إذا نص الرابط هو نفس الـURL الطويل نختصره
  if (a.textContent.trim() === href || /^https?:\/\//.test(a.textContent.trim())) {
    a.textContent = "🔗 " + shortLinkLabel(href);
  }
};

const cleanNode = (node, doc) => {
  [...node.childNodes].forEach((child) => {
    if (child.nodeType === 3) {
      // نص عادي: نحوّل الروابط لعناصر <a> (إذا ما كان أصلاً داخل رابط)
      if (child.parentNode.closest?.("a")) return;
      const text = child.nodeValue;
      if (!URL_RE.test(text)) return;
      URL_RE.lastIndex = 0;
      const frag = doc.createDocumentFragment();
      let last = 0;
      text.replace(URL_RE, (match, _g, offset) => {
        // نقاط/أقواس بنهاية الرابط غالباً مو جزء منه
        const trimmed = match.replace(/[).,،؟?!]+$/, "");
        frag.appendChild(doc.createTextNode(text.slice(last, offset)));
        const a = doc.createElement("a");
        a.textContent = trimmed;
        decorateLink(a, trimmed);
        frag.appendChild(a);
        last = offset + trimmed.length;
        return match;
      });
      frag.appendChild(doc.createTextNode(text.slice(last)));
      child.replaceWith(frag);
      return;
    }

    if (child.nodeType !== 1) {
      child.remove();
      return;
    }

    if (!ALLOWED_TAGS.has(child.tagName)) {
      // وسوم غير مسموحة (script, style, iframe, img...) تنحذف مع محتواها
      // إذا خطرة، وغيرها نبقي نصها فقط
      if (["SCRIPT", "STYLE", "IFRAME", "OBJECT", "EMBED", "IMG", "SVG", "MATH"].includes(child.tagName)) {
        child.remove();
      } else {
        cleanNode(child, doc);
        child.replaceWith(...child.childNodes);
      }
      return;
    }

    [...child.attributes].forEach((attr) => {
      const name = attr.name.toLowerCase();
      if (name === "style") {
        const style = cleanStyle(attr.value);
        if (style) child.setAttribute("style", style);
        else child.removeAttribute("style");
      } else if (child.tagName === "FONT" && (name === "color" || name === "size")) {
        // مسموح
      } else if (name === "dir" || name === "align") {
        // مسموح
      } else if (child.tagName === "A" && name === "href") {
        // يُعالج تحت
      } else {
        child.removeAttribute(attr.name);
      }
    });

    if (child.tagName === "A") {
      const href = child.getAttribute("href");
      if (isSafeUrl(href)) {
        decorateLink(child, href);
      } else {
        child.replaceWith(...child.childNodes);
        return;
      }
    }

    cleanNode(child, doc);
  });
};

export const sanitizeTaskHtml = (html) => {
  if (!html || typeof window === "undefined" || !window.DOMParser) return "";
  const doc = new window.DOMParser().parseFromString(`<div>${html}</div>`, "text/html");
  const root = doc.body.firstChild;
  if (!root) return "";
  cleanNode(root, doc);
  return root.innerHTML;
};

const escapeHtml = (s) =>
  String(s || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

// النص القديم (بدون تنسيق) → HTML يحافظ على الأسطر
export const plainTextToHtml = (text) => escapeHtml(text).replace(/\n/g, "<br>");
